import React, { useState, useEffect } from "react";
import { Heading } from "../components/heading";
import { Button } from "../components/button";
import { BitacoraTimeline } from "../components/BitacoraTimeline";
import { useUser } from "../components/UserContext";
import { Listbox, ListboxOption, ListboxLabel } from "../components/listbox";
import { Field, Label } from "../components/fieldset";
import { FunnelIcon } from "@heroicons/react/24/solid";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { BITACORA_HIDDEN_USER_IDS } from "../utils/bitacora";

const Bitacora = () => {
  const { userData, orgData } = useUser();
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [limitFilter, setLimitFilter] = useState(50);
  const [orgUsers, setOrgUsers] = useState([]);

  // Collection filter options
  const collectionOptions = [
    { value: "all", label: "Todas las colecciones" },
    { value: "clientes", label: "Clientes" },
    { value: "contracts", label: "Contratos" },
    { value: "links", label: "Enlaces" },
  ];

  // Action filter options
  const actionOptions = [
    { value: "all", label: "Todas las acciones" },
    { value: "CREATE", label: "Creaciones" },
    { value: "UPDATE", label: "Actualizaciones" },
    { value: "DELETE", label: "Eliminaciones" },
  ];

  // Limit options
  const limitOptions = [
    { value: 25, label: "25 registros" },
    { value: 50, label: "50 registros" },
    { value: 100, label: "100 registros" },
    { value: 200, label: "200 registros" },
  ];

  // Fetch users from organization
  useEffect(() => {
    const fetchOrgUsers = async () => {
      if (!userData?.org) return;

      try {
        const usersQuery = query(
          collection(db, "usuarios"),
          where("org", "==", userData.org)
        );
        const snapshot = await getDocs(usersQuery);
        const users = snapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        }));
        setOrgUsers(users);
      } catch (error) {
        console.error("Error fetching org users:", error);
      }
    };

    fetchOrgUsers();
  }, [userData]);

  // Build user filter options — current org members first, then accounts that
  // still carry the org field but are no longer in the org's member list.
  const memberUids = new Set(orgData?.usuarios || []);
  const visibleUsers = orgUsers.filter(
    (user) => !BITACORA_HIDDEN_USER_IDS.has(user.uid) && user.rol !== 'client'
  );
  const toOption = (user) => ({
    value: user.uid,
    label: user.nombre || user.correo || user.uid,
  });
  const currentUserOptions = visibleUsers.filter((u) => memberUids.has(u.uid)).map(toOption);
  const formerUserOptions = visibleUsers.filter((u) => !memberUids.has(u.uid)).map(toOption);

  // Build filters object
  const filters = {
    limit: limitFilter,
    ...(collectionFilter !== "all" && { collection: collectionFilter }),
    ...(actionFilter !== "all" && { action: actionFilter }),
    ...(userFilter !== "all" && { userId: userFilter }),
  };

  return (
    <div className="container mx-auto px-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <Heading level={1} className="text-3xl font-bold bg-clip-text text-azul-surelia mb-2">
            Bitácora de Actividad
          </Heading>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Collection Filter */}
          <Field>
            <Label>Colección</Label>
            <Listbox
              value={collectionFilter}
              onChange={setCollectionFilter}
            >
              {collectionOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Field>

          {/* Action Filter */}
          <Field>
            <Label>Tipo de acción</Label>
            <Listbox
              value={actionFilter}
              onChange={setActionFilter}
            >
              {actionOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Field>

          {/* User Filter */}
          <Field>
            <Label>Usuario</Label>
            <Listbox
              value={userFilter}
              onChange={setUserFilter}
            >
              <ListboxOption value="all">
                <ListboxLabel>Todos los usuarios</ListboxLabel>
              </ListboxOption>
              {currentUserOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
              {formerUserOptions.length > 0 && (
                <ListboxOption value="__usuarios_anteriores__" disabled>
                  <ListboxLabel>Usuarios anteriores</ListboxLabel>
                </ListboxOption>
              )}
              {formerUserOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Field>

          {/* Limit Filter */}
          <Field>
            <Label>Mostrar</Label>
            <Listbox
              value={limitFilter}
              onChange={setLimitFilter}
            >
              {limitOptions.map((option) => (
                <ListboxOption key={option.value} value={option.value}>
                  <ListboxLabel>{option.label}</ListboxLabel>
                </ListboxOption>
              ))}
            </Listbox>
          </Field>
        </div>

        {/* Reset Filters */}
        {(collectionFilter !== "all" || actionFilter !== "all" || userFilter !== "all" || limitFilter !== 50) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              color="white"
              onClick={() => {
                setCollectionFilter("all");
                setActionFilter("all");
                setUserFilter("all");
                setLimitFilter(50);
              }}
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {userData?.org ? (
          <BitacoraTimeline filters={filters} />
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              Cargando organización...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bitacora;
